import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  X,
  Phone,
  Award,
  ShieldCheck,
  Sparkles,
  Wrench,
  ThumbsUp,
  Plus,
  Minus,
  Search,
  Zap,
  Layers,
  Ruler,
  Star,
  Gem,
} from "lucide-react";
import SEO from "@/components/SEO";
import BeforeAfter, { PolissageBeforeAfter } from "@/components/BeforeAfter";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY, TESTIMONIALS } from "@/data";

const scrollToQuoteForm = (e) => {
  e.preventDefault();
  document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ============ DATA ============ */

const PILLARS = [
  { icon: Ruler, title: "Mesure d'épaisseur du vernis", text: "Contrôle systématique au jaugeur électronique avant chaque polissage, zéro risque pour votre peinture." },
  { icon: Layers, title: "Polissage multi-étapes", text: "1 à 3 passes selon l'état : composé abrasif, finition, jewelling. Résultat sans hologrammes." },
  { icon: Zap, title: "Machines rotatives & DA", text: "Rupes, Flex, Rupes Bigfoot, pads Lake Country, microfibres neuves à chaque véhicule." },
  { icon: Sparkles, title: "Produits pros haut de gamme", text: "Menzerna, Koch Chemie, CarPro, 3D. Choix précis selon la dureté du vernis." },
  { icon: Search, title: "Éclairage LED inspection", text: "Détection des micro-rayures, hologrammes et défauts invisibles à l'œil nu, panneau par panneau." },
  { icon: Award, title: "Standard concours", text: "5,0 / 35 avis Google. Une finition miroir digne des plateaux d'exposition." },
];

const FORMULAS = [
  {
    id: "eclat",
    name: "Éclat",
    summary: "Ravive la brillance et efface les micro-rayures superficielles. Idéal pour un véhicule bien entretenu.",
    duration: "3h à 4h",
    price: "350",
    highlighted: true,
    ribbon: "Coup de cœur clients",
    tag: "1 étape · One-Step",
    correction: "70%",
    includes: [
      "Lavage préparatoire complet",
      "Décontamination argile (clay bar)",
      "Décontamination fer-out (jantes + carrosserie)",
      "Polissage 1 étape (One-Step)",
      "Nettoyage IPA anti-huiles de polissage",
      "Protection cire hydrophobe 3 mois",
      "Inspection finale sous éclairage LED",
    ],
  },
  {
    id: "correction",
    name: "Correction",
    summary: "Le vrai polissage 2 étapes : élimination des rayures, swirls et hologrammes. Un rendu miroir.",
    duration: "6h à 8h",
    price: "500",
    highlighted: false,
    tag: "2 étapes · Cut + Finish",
    correction: "90%",
    includes: [
      "Formule Éclat +",
      "Mesure d'épaisseur du vernis (jaugeur)",
      "Polissage 2 étapes : abrasif puis finition",
      "Correction des rayures, swirls, oxydation",
      "Élimination des hologrammes",
      "Finition jewelling pads ultra-soft",
      "Photos avant/après sous LED d'inspection",
      "Protection sealant longue durée 6 mois",
    ],
  },
  {
    id: "concours",
    name: "Concours",
    summary: "Restauration niveau exposition : 3 étapes, jewelling profond, préparation céramique parfaite.",
    duration: "10h à 14h",
    price: "650",
    highlighted: false,
    tag: "3 étapes · Concours-ready",
    correction: "95%+",
    includes: [
      "Formule Correction +",
      "Polissage 3 étapes (cut + refine + jewelling)",
      "Correction poussée sur toutes surfaces",
      "Restauration profonde de la brillance miroir",
      "Traitement plastiques extérieurs premium",
      "Polissage phares (si nécessaires)",
      "Surface parfaitement préparée pour céramique",
      "Report photo détaillé du travail effectué",
    ],
  },
];

const ADDONS = [
  { label: "Polissage des phares (paire)", price: "+40€" },
  { label: "Retouche localisée éclat / pierre", price: "+30€" },
  { label: "Décontamination fer intensive complète", price: "+50€" },
  { label: "Frais de déplacement au-delà de 20 km", price: "0,60 €/km" },
];

const CERAMIC_TIERS = [
  {
    duration: "1 an",
    tagline: "Découverte",
    prices: { citadine: "190€", break: "220€", suv: "250€" },
    highlighted: false,
  },
  {
    duration: "3 ans",
    tagline: "Le meilleur rapport",
    prices: { citadine: "450€", break: "520€", suv: "580€" },
    highlighted: true,
  },
  {
    duration: "5 ans",
    tagline: "Protection ultime",
    prices: { citadine: "740€", break: "870€", suv: "1000€" },
    highlighted: false,
  },
];

const BENEFITS = [
  {
    icon: Gem,
    title: "L'effet miroir immédiat",
    text: "Une carrosserie qui reflète comme un miroir. Vos passants s'arrêteront pour regarder votre voiture, garanti.",
  },
  {
    icon: Zap,
    title: "Rayures & swirls effacés",
    text: "70% à 95% des micro-rayures, hologrammes et défauts visibles disparaissent définitivement, pas un simple maquillage.",
  },
  {
    icon: Award,
    title: "Plus-value réelle à la revente",
    text: "Une peinture restaurée peut augmenter la valeur de revente de 1 500 à 5 000€ selon le modèle. L'investissement se rembourse.",
  },
  {
    icon: ShieldCheck,
    title: "Alternative au repeint",
    text: "Un vrai polissage évite bien souvent un repeint à plusieurs milliers d'euros. On sauve le vernis d'origine.",
  },
  {
    icon: Layers,
    title: "Base parfaite pour céramique",
    text: "Un polissage impeccable est la seule vraie préparation pour un traitement céramique qui tient dans le temps.",
  },
  {
    icon: Sparkles,
    title: "Un plaisir retrouvé",
    text: "Redécouvrez la couleur d'origine, la profondeur du vernis, l'émotion des premiers jours en concession.",
  },
];

const COMPARE = [
  {
    aspect: "Objectif",
    classic: "Masquer temporairement avec de la cire",
    vistawash: "Corriger définitivement le vernis en profondeur",
  },
  {
    aspect: "Temps consacré",
    classic: "1h à 2h (superficiel)",
    vistawash: "3h à 14h selon la formule choisie",
  },
  {
    aspect: "Contrôle épaisseur vernis",
    classic: "Aucun, risque de perçage",
    vistawash: "Mesure au jaugeur électronique, panneau par panneau",
  },
  {
    aspect: "Éclairage d'inspection",
    classic: "Lumière ambiante, défauts invisibles",
    vistawash: "LED d'inspection professionnelle, torch swirl finder",
  },
  {
    aspect: "Résultat sur les rayures",
    classic: "Traces revenues sous 2 semaines",
    vistawash: "70% à 95% de défauts réellement éliminés",
  },
  {
    aspect: "Hologrammes / traces de polisseuse",
    classic: "Fréquents (pads mal utilisés)",
    vistawash: "Zéro hologramme, finition jewelling pad ultra-soft",
  },
  {
    aspect: "Protection finale",
    classic: "Cire basique, sans préparation",
    vistawash: "Cire hydrophobe 3 mois, sealant, ou préparation céramique",
  },
  {
    aspect: "Report du travail",
    classic: "Aucun, vous croyez sur parole",
    vistawash: "Photos avant/après sous LED, mesures documentées",
  },
];

const FAQ = [
  {
    q: "Quelle est la différence entre polissage et lustrage ?",
    a: "Le lustrage donne un coup d'éclat en surface avec un produit garni de cire, l'effet dure quelques semaines. Le polissage abrasif retire une micro-couche de vernis pour supprimer réellement les rayures et défauts : le résultat est permanent (sauf nouveaux impacts).",
  },
  {
    q: "Combien d'étapes de polissage pour ma voiture ?",
    a: "Ça dépend de l'état du vernis. Après diagnostic sous LED : véhicule récent bien entretenu → formule Éclat (1 étape) ; véhicule avec swirls et micro-rayures marqués → formule Correction (2 étapes) ; véhicule ancien, oxydé ou destiné à recevoir un traitement céramique → formule Concours (3 étapes). Le diagnostic est offert.",
  },
  {
    q: "Le polissage est-il risqué pour la peinture ?",
    a: "Sans mesure d'épaisseur, oui. Chez VistaWash, chaque véhicule passe au jaugeur électronique avant polissage, nous connaissons exactement l'épaisseur de vernis restante et adaptons les composés et les pads en conséquence. Zéro risque de perçage.",
  },
  {
    q: "Combien de temps dure le résultat d'un polissage ?",
    a: "Il faut distinguer deux choses : le résultat visuel et la protection appliquée par-dessus. Le résultat du polissage lui-même est PERMANENT : une fois les rayures, swirls et hologrammes corrigés, ils ne réapparaissent jamais tant que la peinture n'est pas ré-endommagée par de nouveaux impacts (portière, branches, lavage abrasif ou mauvaise éponge). En revanche, la protection appliquée après le polissage a une durée de vie limitée : cire hydrophobe environ 3 mois, sealant environ 6 mois, céramique 1 an jusqu'à 12 mois, céramique 3 ans jusqu'à 36 mois, et céramique 5 ans jusqu'à 60 mois. Pour conserver l'effet miroir sur la durée, nous recommandons un entretien régulier (lavage main, produits pH neutre) et un renouvellement de la protection à la fin de sa durée de vie.",
  },
  {
    q: "Peut-on polir un véhicule neuf ?",
    a: "Absolument, et c'est même recommandé. Les véhicules neufs sortent souvent d'usine avec des micro-défauts (transport, préparation concession). Un polissage Correction en pré-livraison + traitement céramique garantit une protection maximale dès le départ.",
  },
  {
    q: "Faut-il faire un polissage avant un traitement céramique ?",
    a: "Impérativement. Le céramique fige la surface telle qu'elle est : si vous appliquez de la céramique sur un vernis avec des rayures, elles seront figées définitivement pour 2 à 5 ans. Le polissage est la seule vraie préparation.",
  },
  {
    q: "Les tarifs sont-ils identiques pour tous les véhicules ?",
    a: "Les prix affichés correspondent à une citadine. Un supplément s'applique selon le gabarit : +70€ pour une berline ou un break, +140€ pour un SUV/4x4 ou un utilitaire, car ces véhicules ont une surface plus importante à traiter (jusqu'à +40% de temps).",
  },
  {
    q: "Combien de temps garder ma voiture chez VistaWash ?",
    a: "Formule Éclat : demi-journée. Formule Correction : journée complète. Formule Concours : 1,5 à 2 jours ouvrables. Nous convenons ensemble d'un créneau qui vous arrange.",
  },
];

const Polissage = () => (
  <>
    <SEO
      title="Polissage Automobile & Correction Peinture Charleroi | VistaWash"
      description="Polissage automobile premium à Châtelet & Charleroi. Correction rayures, hologrammes, oxydation. Formules Éclat, Correction, Concours dès 350€. Devis gratuit."
    />
    <Hero />
    <PillarsSection />
    <FormulasSection />
    <PolissageBeforeAfter />
    <BenefitsSection />
    <CompareSection />
    <ReviewsSection />
    <FaqSection />
    <FinalCTA />
  </>
);

/* ============ HERO ============ */
const Hero = () => (
  <section
    data-testid="polish-hero"
    className="relative min-h-[90vh] flex items-center pt-40 pb-24 grain bg-black"
  >
    <div className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1607860108855-64acf2078ed9?auto=format&fit=crop&w=1920&q=90"
        alt="Polissage automobile correction peinture VistaWash Châtelet"
        className="w-full h-full object-cover hero-zoom"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
    </div>

    <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="flex items-center gap-3 mb-6">
        <div className="w-12 h-px bg-[#D90000]" />
        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">
          Polissage & correction peinture · Charleroi & Châtelet
        </span>
      </motion.div>

      <motion.h1
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={1}
        className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.9] max-w-5xl"
      >
        EFFACEZ LES RAYURES. <br />
        RÉVÉLEZ LA <span className="text-[#D90000]">BRILLANCE</span> <br />
        D'ORIGINE.
      </motion.h1>

      <motion.p
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={2}
        className="text-white/75 text-base md:text-lg max-w-2xl mt-8 leading-relaxed"
      >
        Correction professionnelle du vernis, élimination des micro-rayures, hologrammes et oxydation.
        Un vrai polissage multi-étapes réalisé au jaugeur, panneau par panneau, pour un rendu miroir digne d'un plateau d'exposition.
      </motion.p>

      <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex flex-wrap gap-4 mt-10">
        <a
          href="#contact"
          onClick={scrollToQuoteForm}
          data-testid="polish-hero-quote-cta"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
        >
          Demander un devis <ArrowUpRight size={16} />
        </a>
        <a
          href="#formules"
          data-testid="polish-hero-tarifs-cta"
          className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
        >
          Voir les tarifs
        </a>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={4}
        className="mt-16 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-white/50"
      >
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 text-[#FBBC04]">
            {[...Array(5)].map((_, j) => (
              <Star key={j} size={14} fill="currentColor" stroke="none" />
            ))}
          </div>
          <span>5,0 · 35 avis Google</span>
        </div>
        <div className="flex items-center gap-2">
          <Ruler size={14} className="text-[#D90000]" /> Mesure vernis systématique
        </div>
        <div className="flex items-center gap-2">
          <Award size={14} className="text-[#D90000]" /> Finition niveau concours
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ PILLARS ============ */
const PillarsSection = () => (
  <section data-testid="polish-pillars" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Pourquoi VistaWash</span>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[90px] leading-[0.9] mt-6">
          UN VRAI POLISSAGE <br />
          <span className="text-[#D90000]">PAS UN LUSTRAGE</span>
        </h2>
        <p className="text-white/60 mt-8 max-w-2xl mx-auto leading-relaxed">
          La différence entre un polissage professionnel et un simple coup de cire ? Un protocole précis, du matériel haut de gamme, et une expertise qui protège votre vernis.
        </p>
      </motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`polish-pillar-${i}`}
              className="p-8 md:p-10 border border-white/10 bg-[#050505] hover:border-[#D90000]/50 transition-colors"
            >
              <Icon size={32} className="text-[#D90000] mb-6" />
              <h3 className="font-impact text-2xl md:text-3xl mb-3">{p.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{p.text}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============ FORMULAS ============ */
const FormulasSection = () => (
  <section id="formules" data-testid="polish-formules" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Nos formules polissage</span>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[100px] leading-[0.9] mt-6">
          TROIS <span className="text-[#D90000]">NIVEAUX</span>
        </h2>
        <p className="text-white/60 mt-6 max-w-xl mx-auto">
          Choisissez la profondeur de correction adaptée à l'état de votre vernis et à vos attentes.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {FORMULAS.map((f, i) => (
          <motion.article
            key={f.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            data-testid={`polish-formula-${f.id}`}
            className={`relative flex flex-col p-8 md:p-10 border transition-all ${
              f.highlighted
                ? "border-[#D90000] bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] shadow-2xl shadow-[#D90000]/25 md:scale-[1.05] md:-translate-y-2"
                : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
            }`}
          >
            {f.highlighted && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D90000] text-white text-[10px] uppercase tracking-[0.28em] font-bold px-5 py-2 whitespace-nowrap">
                ★ {f.ribbon}
              </div>
            )}

            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D90000] font-semibold mb-3">
              {f.tag}
            </div>
            <h3 className="font-impact text-5xl md:text-6xl leading-none mb-4">{f.name}</h3>
            <p className="text-white/70 text-sm mb-6 leading-relaxed min-h-[64px]">{f.summary}</p>

            {/* Correction meter */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.25em]">
                <span className="text-white/50">Correction des défauts</span>
                <span className="text-[#D90000] font-bold">{f.correction}</span>
              </div>
              <div className="h-1.5 bg-white/10 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#D90000]"
                  style={{ width: f.correction }}
                />
              </div>
            </div>

            <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 mb-4">
              Durée : {f.duration}
            </div>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-white/50 text-sm">À partir de</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-impact text-6xl md:text-7xl text-white leading-none">{f.price}</span>
              <span className="font-impact text-3xl text-[#D90000] leading-none">€</span>
              <span className="text-white/50 text-sm">TTC</span>
            </div>
            <div className="text-white/45 text-xs italic mb-8">(pour une citadine)</div>

            <ul className="space-y-3 mb-8 flex-1">
              {f.includes.map((it) => (
                <li key={it} className="flex items-start gap-3 text-sm text-white/85 leading-relaxed">
                  <Check size={16} className="text-[#D90000] mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              data-testid={`polish-formula-cta-${f.id}`}
              className={`inline-flex items-center justify-center gap-3 w-full text-[12px] uppercase tracking-[0.22em] font-bold px-6 py-4 transition-all border ${
                f.highlighted
                  ? "bg-[#D90000] hover:bg-white hover:text-black text-white border-[#D90000] hover:border-white"
                  : "bg-transparent hover:bg-[#D90000] text-white border-white/25 hover:border-[#D90000]"
              }`}
            >
              Choisir cette formule <ArrowUpRight size={14} />
            </a>
          </motion.article>
        ))}
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="polish-supplements"
        className="mt-10 md:mt-14 border border-white/10 bg-[#0a0a0a] px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8"
      >
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold whitespace-nowrap">
          Suppléments véhicule
        </div>
        <div className="hidden md:block w-px h-6 bg-white/15" />
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-white/75">
          <span><span className="text-white font-bold">Citadine</span>, tarif de base</span>
          <span><span className="text-white font-bold">Berline / Break</span> · <span className="text-[#D90000] font-bold">+70€</span></span>
          <span><span className="text-white font-bold">SUV / 4x4 / Utilitaire</span> · <span className="text-[#D90000] font-bold">+140€</span></span>
        </div>
      </motion.div>

      {/* Passes supplémentaires */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="polish-passes"
        className="mt-6 md:mt-8 border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
      >
        <div className="flex items-start md:items-center gap-3 mb-5 flex-col md:flex-row md:justify-between">
          <div className="flex items-center gap-3">
            <Layers size={16} className="text-[#D90000]" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
              Passe de polissage supplémentaire
            </div>
          </div>
          <span className="text-white/45 text-xs italic">Tarif par passe additionnelle après diagnostic</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 md:gap-4">
          <div className="p-5 border border-white/10 bg-black flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">Citadine</div>
              <div className="text-white text-sm">Une passe supplémentaire</div>
            </div>
            <div className="font-impact text-3xl text-[#D90000] leading-none">+150€</div>
          </div>
          <div className="p-5 border border-white/10 bg-black flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">Berline / Break</div>
              <div className="text-white text-sm">Une passe supplémentaire</div>
            </div>
            <div className="font-impact text-3xl text-[#D90000] leading-none">+200€</div>
          </div>
          <div className="p-5 border border-white/10 bg-black flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">SUV / 4x4</div>
              <div className="text-white text-sm">Une passe supplémentaire</div>
            </div>
            <div className="font-impact text-3xl text-[#D90000] leading-none">+250€</div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="polish-addons"
        className="mt-6 md:mt-8 border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
      >
        <div className="flex items-center gap-3 mb-5">
          <Plus size={16} className="text-[#D90000]" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
            Options & prestations complémentaires
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {ADDONS.map((a) => (
            <li
              key={a.label}
              data-testid={`polish-addon-${a.label}`}
              className="flex items-center justify-between gap-4 py-2 border-b border-white/5 last:border-b-0"
            >
              <span className="text-sm text-white/85 leading-snug">{a.label}</span>
              <span className="font-impact text-lg text-[#D90000] whitespace-nowrap">{a.price}</span>
            </li>
          ))}
        </ul>
        <p className="text-white/45 text-xs mt-5 italic">
          Devis précis communiqué après diagnostic gratuit du vernis sous éclairage LED.
        </p>
      </motion.div>

      {/* Céramique, pricing matrix */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="polish-ceramic-pricing"
        className="mt-6 md:mt-8 border border-[#D90000]/30 bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] p-6 md:p-10"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Gem size={16} className="text-[#D90000]" />
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
                Céramique en option, après polissage
              </div>
            </div>
            <h3 className="font-impact text-3xl md:text-4xl leading-tight">
              PROLONGEZ LE RÉSULTAT <br className="hidden md:block" />
              <span className="text-[#D90000]">JUSQU'À 5 ANS.</span>
            </h3>
          </div>
          <p className="text-white/60 text-sm max-w-sm leading-relaxed">
            Le polissage restaure le vernis. La céramique le fige. Ajoutez une protection longue durée au moment idéal, juste après votre polissage.
          </p>
        </div>

        {/* Header row */}
        <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-3 pb-4 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">Durée de protection</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 text-center">Citadine</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 text-center">Berline / Break</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] font-bold text-center">SUV / 4x4</div>
        </div>

        {/* Rows */}
        {CERAMIC_TIERS.map((tier) => (
          <div
            key={tier.duration}
            data-testid={`ceramic-tier-${tier.duration}`}
            className={`relative grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-3 items-center py-5 md:py-6 border-b border-white/10 last:border-b-0 ${
              tier.highlighted ? "bg-[#D90000]/[0.04] px-4 -mx-4 md:mx-0 md:px-4" : ""
            }`}
          >
            {tier.highlighted && (
              <div className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 hidden md:block">
                <div className="w-1 h-16 bg-[#D90000]" />
              </div>
            )}
            <div className="col-span-2 md:col-span-1 flex items-center gap-3">
              <div className="font-impact text-4xl md:text-5xl leading-none text-white">
                {tier.duration}
              </div>
              {tier.highlighted ? (
                <span className="text-[9px] uppercase tracking-[0.25em] text-[#D90000] font-bold border border-[#D90000]/40 px-2 py-1">
                  ★ {tier.tagline}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {tier.tagline}
                </span>
              )}
            </div>

            {/* Mobile labels + prices */}
            <div className="md:hidden col-span-2 flex justify-between gap-4 mt-2">
              <div className="text-center flex-1">
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/45 mb-1">Citadine</div>
                <div className="font-impact text-2xl text-white">{tier.prices.citadine}</div>
              </div>
              <div className="text-center flex-1">
                <div className="text-[9px] uppercase tracking-[0.2em] text-white/45 mb-1">Break</div>
                <div className="font-impact text-2xl text-white">{tier.prices.break}</div>
              </div>
              <div className="text-center flex-1">
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#D90000] mb-1">SUV</div>
                <div className="font-impact text-2xl text-[#D90000]">{tier.prices.suv}</div>
              </div>
            </div>

            {/* Desktop prices */}
            <div className="hidden md:block text-center">
              <span className="font-impact text-3xl lg:text-4xl text-white">{tier.prices.citadine}</span>
            </div>
            <div className="hidden md:block text-center">
              <span className="font-impact text-3xl lg:text-4xl text-white">{tier.prices.break}</span>
            </div>
            <div className="hidden md:block text-center">
              <span className={`font-impact text-3xl lg:text-4xl ${tier.highlighted ? "text-[#D90000]" : "text-white"}`}>
                {tier.prices.suv}
              </span>
            </div>
          </div>
        ))}

        <p className="text-white/45 text-xs mt-6 italic">
          Tarifs applicables après une prestation de polissage VistaWash. Application dans notre atelier climatisé de Châtelet, véhicule à laisser 24 à 48h selon la protection choisie.
        </p>
      </motion.div>
    </div>
  </section>
);

/* ============ BENEFITS ============ */
const BenefitsSection = () => (
  <section data-testid="polish-benefits" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] relative overflow-hidden">
    <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#D90000]/8 blur-[120px] rounded-full" />
    <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#D90000]/6 blur-[120px] rounded-full" />

    <div className="max-w-[1500px] mx-auto relative">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="max-w-3xl mb-16 md:mb-20"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-px bg-[#D90000]" />
          <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">— Ce que vous y gagnez</span>
        </div>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[90px] leading-[0.9]">
          UNE VOITURE <br />
          <span className="text-[#D90000]">TRANSFORMÉE.</span>
        </h2>
        <p className="text-white/60 mt-8 text-base md:text-lg max-w-2xl leading-relaxed">
          Un vrai polissage n'est pas un simple coup de brillance : c'est une transformation durable qui redonne à votre voiture la profondeur, la valeur et la fierté d'une sortie d'usine.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.article
              key={b.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`polish-benefit-${i}`}
              className="group relative p-8 md:p-10 border border-white/10 bg-black hover:border-[#D90000]/60 transition-all hover:-translate-y-1"
            >
              <span className="absolute top-4 right-6 font-impact text-6xl md:text-7xl text-white/[0.05] leading-none group-hover:text-[#D90000]/20 transition-colors">
                0{i + 1}
              </span>

              <div className="relative">
                <div className="w-14 h-14 flex items-center justify-center border border-[#D90000]/40 bg-[#D90000]/10 mb-6 group-hover:bg-[#D90000] transition-colors">
                  <Icon size={24} className="text-[#D90000] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-impact text-2xl md:text-3xl mb-4 leading-tight">{b.title}</h3>
                <p className="text-white/60 text-sm md:text-[15px] leading-relaxed">{b.text}</p>
              </div>
            </motion.article>
          );
        })}
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="polish-benefits-cta"
        className="mt-14 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 md:p-10 border border-white/10 bg-gradient-to-r from-[#1a0505] to-black"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold mb-3">
            Diagnostic gratuit de votre vernis
          </div>
          <div className="font-impact text-3xl md:text-4xl leading-tight">
            SAVEZ-VOUS CE QUE VOTRE PEINTURE PEUT ENCORE DONNER ?
          </div>
        </div>
        <a
          href="#contact"
          onClick={scrollToQuoteForm}
          data-testid="polish-benefits-cta-btn"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-colors whitespace-nowrap"
        >
          Recevoir mon devis <ArrowUpRight size={16} />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ============ COMPARE ============ */
const CompareSection = () => (
  <section data-testid="polish-compare" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Polissage rapide vs polissage pro</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          POURQUOI CHOISIR <br />
          UN <span className="text-[#D90000]">VRAI POLISSAGE</span> ?
        </h2>
        <p className="text-white/60 mt-8 max-w-2xl mx-auto leading-relaxed">
          Beaucoup vendent "un polissage" pour 80€ en 1 heure. Voici, concrètement, ce qui différencie une vraie correction professionnelle d'un simple coup de machine.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <div className="border border-white/10 bg-[#0a0a0a]">
          <div className="p-6 md:p-8 border-b border-white/10 flex items-center gap-4">
            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
              <X size={20} className="text-white/60" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">Standard</div>
              <div className="font-impact text-2xl md:text-3xl">Polissage rapide</div>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {COMPARE.map((c) => (
              <div key={c.aspect} className="p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">{c.aspect}</div>
                <div className="text-white/60 text-sm md:text-base">{c.classic}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#D90000] bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] shadow-2xl shadow-[#D90000]/20">
          <div className="p-6 md:p-8 border-b border-[#D90000]/40 flex items-center gap-4">
            <div className="w-10 h-10 bg-[#D90000] flex items-center justify-center">
              <Check size={20} className="text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D90000] font-bold">Premium</div>
              <div className="font-impact text-2xl md:text-3xl">VistaWash Polissage</div>
            </div>
          </div>
          <div className="divide-y divide-[#D90000]/15">
            {COMPARE.map((c) => (
              <div key={c.aspect} className="p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D90000] mb-2">{c.aspect}</div>
                <div className="text-white text-sm md:text-base font-medium">{c.vistawash}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============ REVIEWS ============ */
const ReviewsSection = () => (
  <section data-testid="polish-reviews" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-14 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
        <div>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Avis Google</span>
          <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
            ILS NOUS <br />
            <span className="text-[#D90000]">RECOMMANDENT</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-0.5 text-[#FBBC04]">
            {[...Array(5)].map((_, j) => (
              <Star key={j} size={18} fill="currentColor" stroke="none" />
            ))}
          </div>
          <div>
            <div className="font-impact text-2xl text-white">5,0</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">35 avis Google</div>
          </div>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4">
        {TESTIMONIALS.slice(0, 3).map((t, i) => (
          <motion.figure
            key={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            data-testid={`polish-review-${i}`}
            className="p-7 border border-white/10 bg-[#050505] hover:border-[#D90000]/40 transition-colors flex flex-col gap-5"
          >
            <div className="flex items-center gap-3">
              {t.avatar ? (
                <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: t.avatarBg }}>
                  {t.initial}
                </div>
              )}
              <div>
                <div className="text-white font-semibold text-sm">{t.name}</div>
                <div className="text-[11px] text-white/45">{t.meta}</div>
              </div>
            </div>
            <div className="flex gap-0.5 text-[#FBBC04]">
              {[...Array(t.rating)].map((_, j) => (
                <Star key={j} size={14} fill="currentColor" stroke="none" />
              ))}
            </div>
            <blockquote className="text-white/85 text-sm leading-relaxed">"{t.text}"</blockquote>
          </motion.figure>
        ))}
      </div>

      <div className="text-center mt-12">
        <a
          href="https://www.google.com/maps/place/VistaWash+-+Detailing/@50.4051,4.5256,17z/data=!4m8!3m7!1s0x47c237a9391aaedf:0x45cc5306c25c93ba!8m2!3d50.4051!4d4.5256!9m1!1b1"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-4 transition-all border border-[#D90000] hover:border-white"
        >
          Voir tous les avis Google <ArrowUpRight size={14} />
        </a>
      </div>
    </div>
  </section>
);

/* ============ FAQ ============ */
const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="polish-faq" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— FAQ</span>
            <h2 className="font-impact text-4xl md:text-5xl lg:text-[64px] leading-[0.9] mt-6">
              QUESTIONS <br />
              <span className="text-[#D90000]">FRÉQUENTES</span>
            </h2>
          </motion.div>
        </div>
        <div className="md:col-span-8">
          <div className="divide-y divide-white/5">
            {FAQ.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`polish-faq-${i}`}
                className="w-full text-left py-6 group"
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-impact text-xl md:text-2xl leading-tight pr-4 uppercase tracking-wide">
                    {item.q}
                  </h3>
                  <div className="shrink-0 w-8 h-8 border border-white/20 group-hover:border-[#D90000] flex items-center justify-center text-white/60 group-hover:text-[#D90000] transition-colors">
                    {open === i ? <Minus size={14} /> : <Plus size={14} />}
                  </div>
                </div>
                <AnimatePresence>
                  {open === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="text-white/65 leading-relaxed mt-4 max-w-3xl overflow-hidden"
                    >
                      {item.a}
                    </motion.p>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ FINAL CTA + FORM ============ */
const FinalCTA = () => (
  <section id="contact" data-testid="polish-final-cta" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#1a0505] to-black border-t border-[#D90000]/20">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Passez à l'action</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[84px] leading-[0.9] mt-6 mb-8">
          VOTRE PEINTURE <br />
          <span className="text-[#D90000]">MÉRITE MIEUX</span> <br />
          QU'UN COUP DE CIRE.
        </h2>
        <p className="text-white/75 leading-relaxed max-w-md mb-10">
          Diagnostic vernis offert et devis personnalisé. Réponse rapide par WhatsApp, lun–sam 9h–18h. Découvrez ce que votre voiture peut réellement retrouver comme éclat.
        </p>
        <div className="flex flex-wrap gap-4 mb-10">
          <a
            href="#contact-form"
            data-testid="polish-final-devis-cta"
            onClick={scrollToQuoteForm}
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Recevoir mon devis gratuit <ArrowUpRight size={16} />
          </a>
          <a
            href={COMPANY.phone_link}
            data-testid="polish-final-phone-cta"
            className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            <Phone size={16} /> {COMPANY.phone_display}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Atelier</div>
            <div className="text-white text-sm">{COMPANY.address_line1}</div>
            <div className="text-white/70 text-sm">{COMPANY.address_line2}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Email</div>
            <a href={`mailto:${COMPANY.email}`} className="text-white text-sm hover:text-[#D90000] transition-colors">
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <div className="md:col-span-7 bg-black/40 border border-white/10 p-8 md:p-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] mb-2">— Formulaire de devis</div>
        <h3 className="font-impact text-3xl md:text-4xl mb-8 leading-tight">
          Décrivez-nous votre besoin
        </h3>
        <QuoteForm defaultService="Polissage Automobile" />
      </div>
    </div>
  </section>
);

export default Polissage;
